import React, { useEffect, useState } from "react";
import apiService from "../services/apiService";
import {
  Box,
  Typography,
  Button,
  Alert,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  MenuItem,
  Menu,
  Switch,
  CircularProgress,
  TextField,
  Autocomplete,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { ReactComponent as SettingsIcon } from "../Icons/Settings.svg";
import { ThemeProvider } from "@mui/material/styles";
import Table_theme from "../Themes/TableTheme";
import theme from "../Themes/FormThemes";
import { ReactComponent as StartTimeIcon } from "../Icons/Start time.svg";
import { ReactComponent as EndTimeIcon } from "../Icons/End time.svg";
import { ReactComponent as NotesIcon } from "../Icons/Notes.svg";
import { ReactComponent as FailureReasonIcon } from "../Icons/failure_reason.svg";
import { ReactComponent as CloneIcon } from "../Icons/Clone.svg";
import { ReactComponent as SuccessIcon } from "../Icons/Job success.svg";
import { ReactComponent as FailureIcon } from "../Icons/Job Failure.svg";
import { ReactComponent as NilIcon } from "../Icons/Job Nil.svg";
import { ReactComponent as EditIcon } from "../Icons/Edit.svg";
import { ReactComponent as DeleteIcon } from "../Icons/Delete.svg";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

const JobStatusDetails = ({
  jsId,
  parentDate,
  onTimesUpdate,
  onParentRefresh,
}) => {
  const [jobstatus, setJobstatus] = useState([]);
  const [clients, setClients] = useState([]);
  const [environments, setEnvironments] = useState([]);
  const [applications, setApplications] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [substages, setSubstages] = useState([]); // Add substages state
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [confirmClone, setConfirmClone] = useState({
    open: false,
    jsd_id: null,
  });
  const [formData, setFormData] = useState({
    cl_id: "",
    env_id: "",
    app_id: "",
    js_id: jsId || "",
    job_id: "",
    client_job_id: "",
    substage_id: "",
    date_monitored: "",
    start_time: "",
    end_time: "",
    job_status: "",
    err_identified_by: "",
    comments: "",
    step_taken: "",
  });

  const [formErrors, setFormErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [originalFormData, setOriginalFormData] = useState({});

  // Check if form has changed
  const hasFormChanged = () => {
    if (!isEditing) return true;
    return JSON.stringify(formData) !== JSON.stringify(originalFormData);
  };

  const [openDialog, setOpenDialog] = useState(false);
  const [editField, setEditField] = useState(null);
  const [loading, setLoading] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  const defaultColumns = {
    client_name: true,
    environment_name: true,
    application_name: true,
    js_id: true,
    jsd_id: true,
    client_job_id: true,
    substage_id: true,
    substage_desc: true,
    date_monitored: true,
    start_time: true,
    end_time: true,
    run_time: true,
    job_status: true,
    comments: true,
    step_taken: true,
  };

  const [visibleColumns, setVisibleColumns] = useState(() => {
    const saved = localStorage.getItem("jobMonitoringVisibleColumns");
    return saved ? JSON.parse(saved) : defaultColumns;
  });

  useEffect(() => {
    localStorage.setItem(
      "jobMonitoringVisibleColumns",
      JSON.stringify(visibleColumns),
    );
  }, [visibleColumns]);

  const columnLabels = {
    client_name: "Client",
    environment_name: "Environment",
    application_name: "Application",
    js_id: "JobMonitor ID",
    jsd_id: "JM Details ID",
    client_job_id: "Client Job ID",
    substage_id: "Substage ID",
    substage_desc: "Substage Name",
    date_monitored: "Date Monitored",
    start_time: "Start Time",
    end_time: "End Time",
    run_time: "Run Time",
    job_status: "Job Status",
    comments: "Failure Reason",
    step_taken: "Notes",
  };

  const columns = Object.keys(columnLabels);

  const handleOpen = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const getStatusIcon = (status) => {
    switch (status) {
      case "Completed":
        return <SuccessIcon />;
      case "Failed":
        return <FailureIcon />;
      default:
        return <NilIcon />;
    }
  };
  // Enhanced parsePastedDateTime - Handles multiple formats like Job Monitoring page
  const parsePastedDateTime = (input) => {
    if (!input)
      return { success: false, value: "", error: "No input provided" };

    let txt = input
      .replace(/\u00A0/g, " ")
      .replace(/\s+/g, " ")
      .trim();

    // Pattern for "Fri Jan 9 05:15:15 CET 2026" or "Nov 26 02:25:53 PST 2025"
    const patterns = [
      // "Fri Jan 9 05:15:15 CET 2026"
      /^([A-Za-z]{3})\s+([A-Za-z]{3})\s+(\d{1,2})\s+(\d{2}):(\d{2}):(\d{2})\s+([A-Z]{2,5})\s+(\d{4})$/,
      // "Nov 26 02:25:53 PST 2025"
      /^([A-Za-z]{3})\s+(\d{1,2})\s+(\d{2}):(\d{2}):(\d{2})\s+([A-Z]{2,5})\s+(\d{4})$/,
      // "Jan 9 05:15:15 2026" (without timezone)
      /^([A-Za-z]{3})\s+(\d{1,2})\s+(\d{2}):(\d{2}):(\d{2})\s+(\d{4})$/,
      // "12/23/2025 6:30:01 PM" or "12/23/2025 18:30:01"
      /^(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{2}):(\d{2})\s*(AM|PM)?$/i,
    ];

    let parsedDate = null;

    // Try first pattern: "Fri Jan 9 05:15:15 CET 2026"
    const match1 = txt.match(patterns[0]);
    if (match1) {
      const [_, dayName, month, day, hour, min, sec, tz, year] = match1;
      const dateStr = `${month} ${day} ${year} ${hour}:${min}:${sec}`;
      parsedDate = new Date(dateStr);
    }

    // Try second pattern: "Nov 26 02:25:53 PST 2025"
    if (!parsedDate || isNaN(parsedDate)) {
      const match2 = txt.match(patterns[1]);
      if (match2) {
        const [_, month, day, hour, min, sec, tz, year] = match2;
        const dateStr = `${month} ${day} ${year} ${hour}:${min}:${sec}`;
        parsedDate = new Date(dateStr);
      }
    }

    // Try third pattern: "Jan 9 05:15:15 2026"
    if (!parsedDate || isNaN(parsedDate)) {
      const match3 = txt.match(patterns[2]);
      if (match3) {
        const [_, month, day, hour, min, sec, year] = match3;
        const dateStr = `${month} ${day} ${year} ${hour}:${min}:${sec}`;
        parsedDate = new Date(dateStr);
      }
    }

    // Try fourth pattern: "12/23/2025 6:30:01 PM"
    if (!parsedDate || isNaN(parsedDate)) {
      const match4 = txt.match(patterns[3]);
      if (match4) {
        const [_, month, day, year, hour, min, sec, ampm] = match4;
        let hour24 = parseInt(hour);

        // Validate month and day ranges
        const monthNum = parseInt(month);
        const dayNum = parseInt(day);
        if (monthNum < 1 || monthNum > 12) {
          return {
            success: false,
            value: "",
            error: "Invalid month (must be 1-12)",
          };
        }
        if (dayNum < 1 || dayNum > 31) {
          return {
            success: false,
            value: "",
            error: "Invalid day (must be 1-31)",
          };
        }

        // Convert 12-hour to 24-hour format if AM/PM is present
        if (ampm) {
          if (ampm.toUpperCase() === "PM" && hour24 !== 12) {
            hour24 += 12;
          } else if (ampm.toUpperCase() === "AM" && hour24 === 12) {
            hour24 = 0;
          }
        }

        // Validate hour, minute, second ranges
        if (hour24 < 0 || hour24 > 23) {
          return {
            success: false,
            value: "",
            error: "Invalid hour (must be 0-23)",
          };
        }
        if (parseInt(min) < 0 || parseInt(min) > 59) {
          return {
            success: false,
            value: "",
            error: "Invalid minutes (must be 0-59)",
          };
        }
        if (parseInt(sec) < 0 || parseInt(sec) > 59) {
          return {
            success: false,
            value: "",
            error: "Invalid seconds (must be 0-59)",
          };
        }

        // Create date using MM/DD/YYYY format
        parsedDate = new Date(year, month - 1, day, hour24, min, sec);
      }
    }

    if (parsedDate && !isNaN(parsedDate)) {
      // Convert to datetime-local format: YYYY-MM-DDTHH:mm:ss
      const year = parsedDate.getFullYear();
      const month = String(parsedDate.getMonth() + 1).padStart(2, "0");
      const day = String(parsedDate.getDate()).padStart(2, "0");
      const hour = String(parsedDate.getHours()).padStart(2, "0");
      const min = String(parsedDate.getMinutes()).padStart(2, "0");
      const sec = String(parsedDate.getSeconds()).padStart(2, "0");

      return {
        success: true,
        value: `${year}-${month}-${day}T${hour}:${min}:${sec}`,
        error: null,
      };
    }

    // Return error for invalid format
    return {
      success: false,
      value: "",
      error:
        "Invalid datetime format. Supported formats: 'Fri Jan 9 05:15:15 CET 2026', 'Nov 26 02:25:53 PST 2025', 'Jan 9 05:15:15 2026', '12/23/2025 6:30:01 PM'",
    };
  };
  const compactMultilineStyles = {
    "& .MuiInputBase-root": {
      minHeight: "80px",
      // Remove custom fontSize to use default
    },
    "& .MuiInputBase-input": {
      padding: "8px 10px",
      lineHeight: "1.2",
      overflowY: "auto",
    },
    "& .MuiInputLabel-root": {
      // Remove custom fontSize to use default
      top: "-2px",
    },
    "& .MuiInputLabel-shrink": {
      top: "0",
    },
    "& .MuiFormHelperText-root": {
      fontSize: "0.75rem",
    },
  };
  // Input format: backend now sends "YYYY-MM-DDTHH:mm:ss" in UTC → perfect for datetime-local
  const toInputFormat = (dbValue) => {
    if (!dbValue) return "";
    return dbValue.slice(0, 19); // Remove any trailing Z or milliseconds
  };

  useEffect(() => {
    fetchJobstatus();
    fetchClients();
    fetchEnvironments();
    fetchApplications();
    // Don't fetch substages on mount - they should be fetched when a job is selected
  }, [jsId]);

  // Auto-set client when clients are loaded
  useEffect(() => {
    if (clients.length > 0 && !formData.cl_id) {
      setFormData((prev) => ({
        ...prev,
        cl_id: clients[0].cl_id,
      }));
    }
  }, [clients, formData.cl_id]);

  useEffect(() => {
    if (jobstatus.length > 0) {
      const sorted = [...jobstatus].sort((a, b) =>
        (a.substage_id || "").localeCompare(b.substage_id || "", undefined, {
          numeric: true,
        }),
      );
      const firstStart = sorted[0]?.start_time || null;
      const lastEnd = sorted[sorted.length - 1]?.end_time || null;
      if (onTimesUpdate) {
        onTimesUpdate(firstStart, lastEnd);
      }
    } else {
      if (onTimesUpdate) {
        onTimesUpdate(null, null);
      }
    }
  }, [jobstatus, onTimesUpdate]);

  const fetchJobstatus = async () => {
    try {
      setLoading(true);
      const endpoint = jsId
        ? `/jobstatusdetails/js/${jsId}`
        : `/jobstatusdetails`;
      const data = await apiService.get(endpoint);
      setJobstatus(data);
    } catch (err) {
      if (err.message.includes("404")) {
        setJobstatus([]);
        return;
      }
      setErrorMessage(err.message || "Error loading data");
      setTimeout(() => setErrorMessage(""), 5000);
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    try {
      const data = await apiService.get("/jobstatusdetails/clients");
      setClients(data);
    } catch (err) {
      console.error("Failed to load clients");
    }
  };

  const fetchEnvironments = async () => {
    try {
      const data = await apiService.get("/jobstatusdetails/environments");
      setEnvironments(data);
    } catch (err) {
      console.error("Failed to load environments");
    }
  };

  const fetchApplications = async (envId = null) => {
    try {
      let url = "/jobstatusdetails/applications";
      if (envId) {
        url += `?env_id=${envId}`;
      }
      const data = await apiService.get(url);
      setApplications(data);
      return data;
    } catch (err) {
      console.error("Failed to load applications");
      setApplications([]);
      return [];
    }
  };

  const fetchJobs = async (envId = null, appId = null) => {
    try {
      if (!envId || !appId) {
        setJobs([]);
        return [];
      }

      let url = `/jobstatusdetails/jobs?env_id=${envId}&app_id=${appId}`;
      const data = await apiService.get(url);
      setJobs(data);
      return data;
    } catch (err) {
      console.error("Failed to load jobs:", err);
      setJobs([]);
      return [];
    }
  };

  const fetchSubstages = async (jobId = null) => {
    try {
      if (!jobId) {
        setSubstages([]);
        return [];
      }

      const data = await apiService.get(`/substage/${jobId}`); // Changed from /substages to /substage

      setSubstages(data);
      return data;
    } catch (err) {
      // If 404, it means no substages found for this job - this is not an error
      if (err.message && err.message.includes("404")) {
        setSubstages([]);
        return [];
      }
      console.error("Failed to load substages:", err);
      setSubstages([]);
      return [];
    }
  };

  const updateSingleField = async (jsd_id, field, value) => {
    try {
      if (field === "start_time" || field === "end_time") {
        const currentJob = jobstatus.find((job) => job.jsd_id === jsd_id);
        if (!currentJob) {
          setErrorMessage("Record not found in current data");
          setTimeout(() => setErrorMessage(""), 3000);
          return;
        }

        const newStart = field === "start_time" ? value : currentJob.start_time;
        const newEnd = field === "end_time" ? value : currentJob.end_time;

        if (newStart && newEnd && new Date(newStart) > new Date(newEnd)) {
          setErrorMessage("Start time cannot be after End time!");
          setTimeout(() => setErrorMessage(""), 3000);
          return;
        }
      }

      await apiService.patch(`/jobstatusdetails/${jsd_id}/field`, {
        field,
        value: value || null,
      });

      await fetchJobstatus();

      if (jsId) {
        await apiService.patch(`/jobmonitoring/${jsId}/update-times`);
      }

      if (onTimesUpdate) {
        const freshDetails = await apiService.get(
          `/jobstatusdetails/js/${jsId}`,
        );

        const sorted = freshDetails.sort((a, b) =>
          (a.substage_id || "").localeCompare(b.substage_id || "", undefined, {
            numeric: true,
          }),
        );

        onTimesUpdate(
          sorted[0]?.start_time || null,
          sorted[sorted.length - 1]?.end_time || null,
        );
      }

      if (onParentRefresh) onParentRefresh();

      setSuccessMessage(`${field.replace("_", " ")} updated successfully`);
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (err) {
      setErrorMessage("Error updating field: " + err.message);
      setTimeout(() => setErrorMessage(""), 3000);
    }
  };

  const validate = () => {
    const errors = {};
    // Client is auto-set and frozen, so no validation needed
    if (!formData.env_id) errors.env_id = "Environment is required.";
    if (!formData.app_id) errors.app_id = "Application is required.";
    if (!formData.job_id) errors.job_id = "Job ID is required.";
    if (!formData.substage_id) errors.substage_id = "Substage is required."; // Add substage validation
    if (!formData.date_monitored)
      errors.date_monitored = "Date Monitored is required."; // Add date validation

    if (formData.start_time && formData.end_time) {
      if (new Date(formData.start_time) > new Date(formData.end_time)) {
        errors.start_time = "Start time cannot be after end time.";
      }
    }
    return errors;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // If environment changes, refresh applications for that environment
    if (name === "env_id") {
      fetchApplications(value);
      // Reset application and job selection when environment changes
      setFormData((prev) => ({
        ...prev,
        app_id: "",
        job_id: "",
        client_job_id: "",
      }));
      setJobs([]);
    }

    // If application changes, refresh jobs
    if (name === "app_id") {
      if (formData.env_id && value) {
        fetchJobs(formData.env_id, value);
      } else {
        setJobs([]);
      }
      // Reset job selection when application changes
      setFormData((prev) => ({ ...prev, job_id: "", client_job_id: "" }));
    }
  };

  // Cascading handlers for Autocomplete components
  const handleEnvironmentChange = async (event, newValue) => {
    const envId = newValue ? newValue.env_id : "";
    setFormData((prev) => ({
      ...prev,
      env_id: envId,
      app_id: "", // Reset application when environment changes
      job_id: "", // Reset job when environment changes
      client_job_id: "", // Reset client job when environment changes
    }));

    // Fetch applications for the selected environment
    if (envId) {
      await fetchApplications(envId);
    } else {
      setApplications([]);
    }

    // Clear dependent dropdowns
    setJobs([]);
  };

  const handleApplicationChange = async (event, newValue) => {
    const appId = newValue ? newValue.app_id : "";
    setFormData((prev) => ({
      ...prev,
      app_id: appId,
      job_id: "", // Reset job when application changes
      client_job_id: "", // Reset client job when application changes
    }));

    // Fetch jobs for the selected environment and application
    if (formData.env_id && appId) {
      await fetchJobs(formData.env_id, appId);
    } else {
      setJobs([]);
    }
  };

  const handleJobChange = async (event, newValue) => {
    const jobId = newValue ? newValue.job_id : "";
    const clientJobId = newValue ? newValue.client_job_id : "";
    setFormData((prev) => ({
      ...prev,
      job_id: jobId,
      client_job_id: clientJobId,
      substage_id: "", // Reset substage when job changes
    }));

    // Fetch substages for the selected job
    if (jobId) {
      await fetchSubstages(jobId);
    } else {
      setSubstages([]);
    }
  };

  const handleDialogOpen = () => {
    // Get current date in YYYY-MM-DD format
    const today = new Date();
    const currentDate = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;

    setFormData({
      cl_id: clients.length > 0 ? clients[0].cl_id : "", // Auto-set client
      env_id: "",
      app_id: "",
      js_id: jsId || "",
      job_id: "",
      client_job_id: "",
      substage_id: "",
      date_monitored: currentDate, // Set current date as default
      start_time: "",
      end_time: "",
      job_status: "",
      err_identified_by: "",
      comments: "",
      step_taken: "",
    });
    setIsEditing(false);
    setFormErrors({});

    // Clear dependent dropdowns
    setApplications([]);
    setJobs([]);

    setOpenDialog(true);
  };

  const handleEdit = async (job) => {
    setIsEditing(true);

    // First, load dependent data
    if (job.env_id) {
      await fetchApplications(job.env_id);
      if (job.app_id) {
        await fetchJobs(job.env_id, job.app_id);
        // Fetch substages for the job
        if (job.job_id) {
          await fetchSubstages(job.job_id);
        }
      }
    }

    // Then set form data after dependent data is loaded
    const editFormData = {
      ...job,
      date_monitored: job.date_monitored_edit || "",
      start_time: job.start_time || "",
      end_time: job.end_time || "",
    };

    setFormData(editFormData);
    setOriginalFormData({ ...editFormData }); // Store original for change detection

    setOpenDialog(true);
  };

  const handleToggleStatus = async (job) => {
    try {
      const data = await apiService.patch(
        `/jobstatusdetails/${job.jsd_id}/toggle-status`,
      );

      await fetchJobstatus();

      setSuccessMessage(`Job status updated to "${data.job_status}"`);
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (err) {
      setErrorMessage("Error toggling status: " + err.message);
      setTimeout(() => setErrorMessage(""), 3000);
    }
  };

  const handleDeleteClick = (jsd_id) => {
    setDeleteId(jsd_id);
    setOpenDeleteDialog(true);
  };

  const confirmDelete = async () => {
    try {
      setDeleteLoading(true);

      await apiService.delete(`/jobstatusdetails/${deleteId}`);

      await fetchJobstatus();

      if (jsId) {
        await apiService.patch(`/jobmonitoring/${jsId}/update-times`);
      }

      if (onParentRefresh) onParentRefresh();

      setSuccessMessage("Deleted successfully");
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (err) {
      setErrorMessage("Delete failed: " + err.message);
      setTimeout(() => setErrorMessage(""), 5000);
    } finally {
      setDeleteLoading(false);
      setOpenDeleteDialog(false);
      setDeleteId(null);
    }
  };

  const cancelDelete = () => {
    setOpenDeleteDialog(false);
    setDeleteId(null);
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = validate();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    try {
      if (isEditing) {
        await apiService.put(`/jobstatusdetails/${formData.jsd_id}`, formData);
      } else {
        await apiService.post(`/jobstatusdetails`, formData);
      }

      setSuccessMessage(
        isEditing ? "Updated successfully" : "Added successfully",
      );
      setOpenDialog(false);
      await fetchJobstatus();

      if (jsId) {
        await apiService.patch(`/jobmonitoring/${jsId}/update-times`);
      }

      if (onParentRefresh) onParentRefresh();

      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (err) {
      setErrorMessage("Save failed: " + err.message);
      setTimeout(() => setErrorMessage(""), 5000);
    }
  };

  return (
    <Box sx={{ py: 3, mt: -4 }}>
      {successMessage && <Alert severity="success">{successMessage}</Alert>}
      {errorMessage && <Alert severity="error">{errorMessage}</Alert>}

      <Box display="flex" justifyContent="flex-start" gap={0} mb={1} mt={2}>
        <Button
          onClick={handleOpen}
          sx={{
            color: "#666",
            height: "35px",
            width: "10px",
            borderRadius: "8px",
            "&:hover": {
              color: "#1976d2",
            },
          }}
        >
          <SettingsIcon style={{ width: 17, height: 17 }} />
        </Button>
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          PaperProps={{
            sx: {
              mt: 1,
              p: 1,
              width: 220,
              maxHeight: 300,
              overflowY: "auto",
              borderRadius: 2,
            },
          }}
        >
          {columns.map((col) => (
            <MenuItem key={col} disableGutters sx={{ p: 0, mb: 1 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  width: "100%",
                  border: "1px solid #ccc",
                  borderRadius: 1.5,
                  px: 2,
                  py: 1,
                  backgroundColor: "#fff",
                }}
              >
                <Typography sx={{ fontSize: "0.8rem", fontWeight: "bold" }}>
                  {columnLabels[col]}
                </Typography>
                <Switch
                  size="small"
                  checked={visibleColumns[col]}
                  onChange={(e) =>
                    setVisibleColumns((prev) => ({
                      ...prev,
                      [col]: e.target.checked,
                    }))
                  }
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": { color: "#2DB0E4" },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                      backgroundColor: "#2DB0E4",
                    },
                  }}
                />
              </Box>
            </MenuItem>
          ))}
        </Menu>

        <Button
          onClick={handleDialogOpen}
          sx={{
            color: "#666",
            fontWeight: "bold",
            borderRadius: "8px",
            height: "35px",
            minWidth: "50px",
            px: 1.5,
            textTransform: "none",
            fontSize: "12px",
            "&:hover": {
              color: "#1976d2",
            },
          }}
        >
          Add +
        </Button>
      </Box>

      <ThemeProvider theme={Table_theme}>
        <Paper elevation={3}>
          <TableContainer sx={{ maxHeight: "70vh" }}>
            <Table stickyHeader>
              <TableHead sx={{ bgcolor: "#f5f5f5" }}>
                <TableRow>
                  <TableCell>Actions</TableCell>
                  {columns.map(
                    (col) =>
                      visibleColumns[col] && (
                        <TableCell key={col}>{columnLabels[col]}</TableCell>
                      ),
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell
                      colSpan={columns.length + 1}
                      sx={{
                        position: "sticky",
                        left: 0,
                        textAlign: "left",
                        padding: "20px",
                        backgroundColor: "white",
                        zIndex: 1,
                      }}
                    >
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : jobstatus.length > 0 ? (
                  jobstatus
                    .slice()
                    .sort((a, b) =>
                      (a.substage_id || "").localeCompare(
                        b.substage_id || "",
                        undefined,
                        {
                          numeric: true,
                        },
                      ),
                    )
                    .map((job) => (
                      <TableRow key={job.jsd_id}>
                        <TableCell>
                          <Box display="flex" gap={1} flexWrap="nowrap">
                            <IconButton
                              size="small"
                              onClick={() =>
                                setEditField({
                                  jsd_id: job.jsd_id,
                                  field: "start_time",
                                  value: toInputFormat(job.start_time),
                                })
                              }
                              title="Update Start Time"
                            >
                              <StartTimeIcon />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() =>
                                setEditField({
                                  jsd_id: job.jsd_id,
                                  field: "end_time",
                                  value: toInputFormat(job.end_time),
                                })
                              }
                              title="Update End Time"
                            >
                              <EndTimeIcon />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleToggleStatus(job)}
                              title="Toggle Status"
                            >
                              {getStatusIcon(job.job_status)}
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() =>
                                setEditField({
                                  jsd_id: job.jsd_id,
                                  field: "comments",
                                  value: job.comments || "",
                                })
                              }
                              title="Edit Failure Reason"
                            >
                              <FailureReasonIcon />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() =>
                                setEditField({
                                  jsd_id: job.jsd_id,
                                  field: "step_taken",
                                  value: job.step_taken || "",
                                })
                              }
                              title="Edit Notes"
                            >
                              <NotesIcon />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() =>
                                setConfirmClone({
                                  open: true,
                                  jsd_id: job.jsd_id,
                                })
                              }
                              title="Clone"
                            >
                              <CloneIcon />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteClick(job.jsd_id)}
                              sx={{ color: "error.main" }}
                              title="Delete"
                            >
                              <DeleteIcon />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleEdit(job)}
                              sx={{ color: "primary.main" }}
                              title="Edit"
                            >
                              <EditIcon />
                            </IconButton>
                          </Box>
                        </TableCell>

                        {visibleColumns.client_name && (
                          <TableCell>{job.client_name || "NA"}</TableCell>
                        )}
                        {visibleColumns.environment_name && (
                          <TableCell>{job.environment_name || "NA"}</TableCell>
                        )}
                        {visibleColumns.application_name && (
                          <TableCell>{job.application_name || "NA"}</TableCell>
                        )}
                        {visibleColumns.js_id && (
                          <TableCell>{job.js_id}</TableCell>
                        )}
                        {visibleColumns.jsd_id && (
                          <TableCell>{job.jsd_id}</TableCell>
                        )}
                        {visibleColumns.client_job_id && (
                          <TableCell>{job.client_job_id || "NA"}</TableCell>
                        )}
                        {visibleColumns.substage_id && (
                          <TableCell>{job.substage_id || "NA"}</TableCell>
                        )}
                        {visibleColumns.substage_desc && (
                          <TableCell>{job.substage_desc || "NA"}</TableCell>
                        )}
                        {visibleColumns.date_monitored && (
                          <TableCell>{job.date_monitored || "NA"}</TableCell>
                        )}
                        {visibleColumns.start_time && (
                          <TableCell>{job.start_time || "NA"}</TableCell>
                        )}
                        {visibleColumns.end_time && (
                          <TableCell>{job.end_time || "NA"}</TableCell>
                        )}
                        {visibleColumns.run_time && (
                          <TableCell>{job.run_time || "NA"}</TableCell>
                        )}
                        {visibleColumns.job_status && (
                          <TableCell>{job.job_status || "Nil"}</TableCell>
                        )}
                        {visibleColumns.comments && (
                          <TableCell>{job.comments || ""}</TableCell>
                        )}
                        {visibleColumns.step_taken && (
                          <TableCell>{job.step_taken || ""}</TableCell>
                        )}
                      </TableRow>
                    ))
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={columns.length + 1}
                      sx={{
                        position: "sticky",
                        left: 0,
                        textAlign: "left",
                        padding: "20px",
                        backgroundColor: "white",
                        zIndex: 1,
                      }}
                    >
                      No job status details found.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </ThemeProvider>

      {/* Main Add/Edit Dialog */}
      <ThemeProvider theme={theme}>
        <Dialog
          open={openDialog}
          // onClose={() => setOpenDialog(false)}
          onClose={(event, reason) => {
            if (reason === "backdropClick") return;
            setOpenDialog(false);
          }}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>
            {isEditing
              ? `Edit Substage Details – ${formData.substage_desc || ""}`
              : "Add Job Monitoring Details"}
            <IconButton
              onClick={() => setOpenDialog(false)}
              sx={{ position: "absolute", right: 8, top: 8 }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(5, 1fr)",
                gap: 2,
              }}
            >
              <TextField
                select
                label="Client"
                name="cl_id"
                value={formData.cl_id}
                onChange={handleChange}
                error={!!formErrors.cl_id}
                helperText={formErrors.cl_id}
                disabled={true}
                InputProps={{
                  readOnly: true,
                }}
                sx={{
                  "& .MuiInputBase-input": {
                    backgroundColor: "white",
                  },
                }}
              >
                {clients.map((c) => (
                  <MenuItem key={c.cl_id} value={c.cl_id}>
                    {c.name || c.cl_id}
                  </MenuItem>
                ))}
              </TextField>
              {/* Environment */}
              <Autocomplete
                options={environments}
                getOptionLabel={(option) => option.name || ""}
                value={
                  environments.find((env) => env.env_id === formData.env_id) ||
                  null
                }
                onChange={handleEnvironmentChange}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Environment"
                    error={!!formErrors.env_id}
                    helperText={formErrors.env_id}
                    disabled={isEditing}
                    InputProps={{
                      ...params.InputProps,
                      readOnly: isEditing,
                    }}
                    sx={{
                      "& .MuiInputBase-input": {
                        fontSize: "0.875rem",
                        backgroundColor: isEditing ? "white" : "white",
                      },
                    }}
                  />
                )}
                disabled={isEditing || environments.length === 0}
                slotProps={{
                  paper: {
                    sx: {
                      "& .MuiAutocomplete-option": {
                        fontSize: "0.875rem", // 14px
                      },
                    },
                  },
                }}
                sx={{
                  "& .MuiInputBase-input": {
                    fontSize: "0.875rem", // 14px
                  },
                }}
              />

              {/* Application */}
              <Autocomplete
                options={applications}
                getOptionLabel={(option) => option.name || ""}
                value={
                  applications.find((app) => app.app_id === formData.app_id) ||
                  null
                }
                onChange={handleApplicationChange}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Application"
                    error={!!formErrors.app_id}
                    helperText={formErrors.app_id || !formData.env_id}
                    disabled={isEditing}
                    InputProps={{
                      ...params.InputProps,
                      readOnly: isEditing,
                    }}
                    sx={{
                      "& .MuiInputBase-input": {
                        fontSize: "0.875rem",
                        backgroundColor: isEditing ? "white" : "white",
                      },
                    }}
                  />
                )}
                disabled={isEditing || !formData.env_id || applications.length === 0}
                noOptionsText={
                  !formData.env_id
                    ? "Please select an environment first"
                    : "No applications available"
                }
                slotProps={{
                  paper: {
                    sx: {
                      '& .MuiAutocomplete-option': {
                        fontSize: '0.875rem',
                      },
                    },
                  },
                }}
              />

              <TextField 
                label="JS ID" 
                value={jsId} 
                disabled 
                InputProps={{
                  readOnly: true,
                }}
                sx={{
                  "& .MuiInputBase-input": {
                    fontSize: "0.875rem", // 14px
                  },
                }}
              />

              {/* Job ID */}
              <Autocomplete
                options={jobs}
                getOptionLabel={(option) =>
                  `${option.job_id} - ${option.jobs_script || ""}`
                }
                value={
                  jobs.find((job) => job.job_id === formData.job_id) || null
                }
                onChange={handleJobChange}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Job ID"
                    error={!!formErrors.job_id}
                    helperText={formErrors.job_id || !formData.app_id}
                    disabled={isEditing}
                    InputProps={{
                      ...params.InputProps,
                      readOnly: isEditing,
                    }}
                    sx={{
                      "& .MuiInputBase-input": {
                        fontSize: "0.875rem",
                        backgroundColor: isEditing ? "white" : "white",
                      },
                    }}
                  />
                )}
                disabled={isEditing || !formData.app_id || jobs.length === 0}
                noOptionsText={
                  !formData.app_id
                    ? "Please select an application first"
                    : "No jobs available"
                }
                slotProps={{
                  paper: {
                    sx: {
                      "& .MuiAutocomplete-option": {
                        fontSize: "0.875rem", // 14px
                      },
                    },
                  },
                }}
                sx={{
                  "& .MuiInputBase-input": {
                    fontSize: "0.875rem", // 14px
                  },
                }}
              />

              <TextField
                label="Client Job ID"
                name="client_job_id"
                value={formData.client_job_id}
                disabled={true}
                InputProps={{
                  readOnly: true,
                }}
                sx={{
                  "& .MuiInputBase-input": {
                    backgroundColor: "white",
                  },
                }}
              />

              {/* Substage Dropdown */}
              <Autocomplete
                options={substages}
                getOptionLabel={(option) =>
                  `${option.substage_id} - ${option.substage_desc || ""}`
                }
                value={
                  substages.find(
                    (s) => s.substage_id === formData.substage_id,
                  ) || null
                }
                onChange={(event, newValue) => {
                  setFormData((prev) => ({
                    ...prev,
                    substage_id: newValue ? newValue.substage_id : "",
                  }));
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Substage"
                    error={!!formErrors.substage_id}
                    helperText={formErrors.substage_id}
                  />
                )}
                disabled={!formData.job_id || substages.length === 0}
                noOptionsText={
                  !formData.job_id
                    ? "Please select a job first"
                    : "No substages available for this job"
                }
                slotProps={{
                  paper: {
                    sx: {
                      "& .MuiAutocomplete-option": {
                        fontSize: "0.875rem", // 14px
                      },
                    },
                  },
                }}
                sx={{
                  "& .MuiInputBase-input": {
                    fontSize: "0.875rem", // 14px
                  },
                }}
              />

              <TextField
                type="date"
                label="Date Monitored"
                value={formData.date_monitored || ""}
                onChange={(e) =>
                  setFormData({ ...formData, date_monitored: e.target.value })
                }
                InputLabelProps={{ shrink: true }}
                error={!!formErrors.date_monitored}
                helperText={formErrors.date_monitored}
              />

              <TextField
                type="datetime-local"
                label="Start Time"
                value={formData.start_time || ""}
                onChange={(e) =>
                  setFormData({ ...formData, start_time: e.target.value })
                }
                onPaste={(e) => {
                  e.preventDefault();
                  const pastedText = (
                    e.clipboardData || window.clipboardData
                  ).getData("text");
                  const result = parsePastedDateTime(pastedText);

                  if (result.success) {
                    setFormData((prev) => ({
                      ...prev,
                      start_time: result.value,
                    }));
                  } else {
                    // Show error message if parsing failed
                    setErrorMessage(result.error || "Invalid datetime format");
                    setTimeout(() => setErrorMessage(""), 3000);
                  }
                }}
                InputLabelProps={{ shrink: true }}
                InputProps={{ step: 1 }} // enables seconds
                error={!!formErrors.start_time}
                helperText={formErrors.start_time}
                fullWidth
              />

              <TextField
                type="datetime-local"
                label="End Time"
                value={formData.end_time || ""}
                onChange={(e) =>
                  setFormData({ ...formData, end_time: e.target.value })
                }
                onPaste={(e) => {
                  e.preventDefault();
                  const pastedText = (
                    e.clipboardData || window.clipboardData
                  ).getData("text");
                  const result = parsePastedDateTime(pastedText);

                  if (result.success) {
                    setFormData((prev) => ({
                      ...prev,
                      end_time: result.value,
                    }));
                  } else {
                    // Show error message if parsing failed
                    setErrorMessage(result.error || "Invalid datetime format");
                    setTimeout(() => setErrorMessage(""), 3000);
                  }
                }}
                InputLabelProps={{ shrink: true }}
                InputProps={{ step: 1 }}
                fullWidth
              />
              <TextField
                select
                label="Job Status"
                name="job_status"
                value={formData.job_status}
                onChange={handleChange}
              >
                <MenuItem value="Completed">Completed</MenuItem>
                <MenuItem value="Failed">Failed</MenuItem>
                <MenuItem value="">Nil</MenuItem>
              </TextField>
              <TextField
                label="Error Identified By"
                name="err_identified_by"
                value={formData.err_identified_by}
                onChange={handleChange}
              />

              <TextField
                label="Steps Taken (Notes)"
                name="step_taken"
                value={formData.step_taken}
                onChange={handleChange}
                multiline
              />
            </Box>
            <TextField
              label="Comments (Failure Reason)"
              name="comments"
              value={formData.comments}
              onChange={handleChange}
              multiline
              sx={{ ...compactMultilineStyles, width: "100%" }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={isEditing && !hasFormChanged()}
            >
              {isEditing ? "Update" : "Add"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Inline Edit Dialog */}
        <Dialog
          open={!!editField}
          onClose={() => setEditField(null)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            {editField?.field === "comments"
              ? "EDIT FAILURE REASON"
              : editField?.field === "step_taken"
                ? "EDIT NOTES"
                : `Edit ${editField?.field.replace("_", " ").toUpperCase()}`}
          </DialogTitle>
          <DialogContent>
            {["start_time", "end_time"].includes(editField?.field) ? (
              <TextField
                autoFocus
                type="datetime-local"
                fullWidth
                value={editField.value || ""}
                onChange={(e) =>
                  setEditField({ ...editField, value: e.target.value })
                }
                onPaste={(e) => {
                  e.preventDefault();
                  const pastedText = (
                    e.clipboardData || window.clipboardData
                  ).getData("text");
                  const result = parsePastedDateTime(pastedText);

                  if (result.success) {
                    setEditField((prev) => ({ ...prev, value: result.value }));
                  } else {
                    // Show error message if parsing failed
                    setErrorMessage(result.error || "Invalid datetime format");
                    setTimeout(() => setErrorMessage(""), 3000);
                  }
                }}
              />
            ) : (
              <TextField
                autoFocus
                fullWidth
                margin="dense"
                variant="outlined"
                label={
                  editField?.field === "comments"
                    ? "Failure Reason"
                    : editField?.field === "step_taken"
                      ? "Notes"
                      : editField?.field?.replace("_", " ").toUpperCase()
                }
                value={editField?.value || ""}
                onChange={(e) =>
                  setEditField({ ...editField, value: e.target.value })
                }
                InputLabelProps={{
                  shrink: true,
                }}
                sx={{ mt: 2 }}
              />
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditField(null)}>Cancel</Button>
            <Button
              variant="contained"
              onClick={async () => {
                await updateSingleField(
                  editField.jsd_id,
                  editField.field,
                  editField.value,
                );
                setEditField(null);
              }}
            >
              Save
            </Button>
          </DialogActions>
        </Dialog>

        {/* Clone Confirmation */}
        <Dialog
          open={confirmClone.open}
          onClose={() => setConfirmClone({ open: false, jsd_id: null })}
        >
          <DialogTitle sx={{ fontWeight: "bold" }}>Confirm Clone</DialogTitle>
          <Typography fontSize="0.9rem">
            Are you sure you want to clone this substage?
          </Typography>
          <DialogActions>
            <Button
              onClick={() => setConfirmClone({ open: false, jsd_id: null })}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={async () => {
                try {
                  await apiService.post(
                    `/jobstatusdetails/${confirmClone.jsd_id}/clone`,
                  );
                  await fetchJobstatus();

                  if (jsId) {
                    await apiService.patch(
                      `/jobmonitoring/${jsId}/update-times`,
                    );
                  }
                  if (onParentRefresh) onParentRefresh();
                } catch (err) {
                  setErrorMessage("Clone failed: " + err.message);
                  setTimeout(() => setErrorMessage(""), 3000);
                } finally {
                  setConfirmClone({ open: false, jsd_id: null });
                }
              }}
            >
              Confirm
            </Button>
          </DialogActions>
        </Dialog>
        <Dialog
          open={openDeleteDialog}
          onClose={() => {
            setOpenDeleteDialog(false);
            setDeleteId(null);
          }}
        >
          <DialogTitle sx={{ fontWeight: "bold" }}>Confirm Delete</DialogTitle>

          <DialogContent>
            <Typography fontSize="0.9rem">
              Are you sure you want to delete Substage{" "}
              <strong>
                {jobstatus.find((job) => job.jsd_id === deleteId)
                  ?.substage_desc || ""}
              </strong>{" "}
              (ID: <strong>{deleteId}</strong>)?
            </Typography>
          </DialogContent>

          <DialogActions>
            <Button
              onClick={() => {
                setOpenDeleteDialog(false);
                setDeleteId(null);
              }}
            >
              Cancel
            </Button>

            <Button
              variant="contained"
              onClick={async () => {
                try {
                  await apiService.delete(`/jobstatusdetails/${deleteId}`);

                  await fetchJobstatus();

                  if (jsId) {
                    await apiService.patch(
                      `/jobmonitoring/${jsId}/update-times`,
                    );
                  }

                  if (onParentRefresh) onParentRefresh();
                } catch (err) {
                  setErrorMessage("Delete failed: " + err.message);
                  setTimeout(() => setErrorMessage(""), 3000);
                } finally {
                  setOpenDeleteDialog(false);
                  setDeleteId(null);
                }
              }}
            >
              Confirm
            </Button>
          </DialogActions>
        </Dialog>
      </ThemeProvider>
    </Box>
  );
};

export default JobStatusDetails;
